"use strict";

import angular from "angular";

import template from "./indicator-values.component.html";
import "./themes/indicator-values.component-dark.scss";
import "./themes/indicator-values.component.scss";

import _ from "lodash";
import { Point } from "../../../gateway/Point/Point";
import { PointCalculated } from "../../../gateway/PointCalculated/PointCalculated";
import { Serie } from "../../../gateway/Serie/Serie";
import { CalculatingMachine } from "../../../presentation/services/CalculatingMachine/CalculatingMachine";
import { FormatUnits } from "../../../presentation/services/FormatUnits/FormatUnits";

class IndicatorValuesComponent {
    constructor($mdMedia, $stateParams, $filter) {
        this.$mdMedia = $mdMedia;
        this.$stateParams = $stateParams;
        this.$filter = $filter;
        this.PointDomain = new Point();
        this.PointCalculatedDomain = new PointCalculated();
        this.SerieDomain = new Serie();
        this.CalculatingMachine = new CalculatingMachine({
            Serie: this.SerieDomain,
            Point: this.PointDomain,
            PointCalculated: this.PointCalculatedDomain
        });
        this.FormatUnits = new FormatUnits($filter);

        this.companyId = this.$stateParams.id;
        this.isHighlight = false;
        this._currentPeriod = {};
        this._currentComparation = {};
    }

    $onInit() {
        this._setData();
    }

    $onChanges(changes) {
        if (
            !_.isEmpty(_.get(changes, "currentComparation.previousValue")) ||
            !_.isEmpty(_.get(changes, "currentPeriod.previousValue"))
        ) {
            this._setData();
        }

        if (this.fakeData && _.get(changes, "seriesData.currentValue.length"))
            this._setData();

        if (
            !this.fakeData &&
            _.get(changes, "seriesData.currentValue.length") &&
            _.get(changes, "seriesData.previousValue.length")
        )
            this._setData();
    }

    async _setData() {
        this._currentPeriod = angular.copy(this.currentPeriod);
        this._currentComparation = angular.copy(this.currentComparation);
        const points = this._getPoints();
        if (points) {
            this.seriesComparations = points.map((point) => {
                return {
                    ...point,
                    current: this._formatValuePoint(point.unit, point.current)
                };
            });
            this.seriesComparations = this.seriesComparations.map((serie) => {
                return {
                    ...serie,
                    i: this.visualization.series.findIndex(
                        (v) => v == serie._id
                    )
                };
            });
        }
    }

    _formatValuePoint(unit = "currency", value = 0) {
        let fixedValue = 0;

        if (["percentage", "number"].includes(unit)) fixedValue = 2;

        return this.FormatUnits.formatValue(unit, value, fixedValue);
    }

    _getComparationPoints(seriesComparations) {
        this.CalculatingMachine.setSource(this.visualization.sources[0]);
        const xFilter = this._currentPeriod;
        const yFilter = this._currentComparation;
        if (this.budget) {
            xFilter.scenario._id = this.budget;
            yFilter.scenario._id = this.budget;
        }
        return this.CalculatingMachine.comparation({
            series: seriesComparations,
            companyId: this.companyId,
            xFilter,
            yFilter,
            fakeData: this.fakeData,
            accumulated: this.isAccumulated,
            isGroupCompanies: _.get(this.companyData, "isGroup"),
            options: {
                groupFilterOper: this.currentFilterGroup,
                totalGroupCompanies: _.get(
                    this.companyData,
                    "companyIds.length",
                    0
                )
            }
        });
    }

    async _getFakePoints() {
        const seriesComparations = await this._getSeriesComparationsFromCloud(
            this.visualization,
            this.$mdMedia("xs")
        );

        if (seriesComparations.length)
            return this._getComparationPoints(seriesComparations);
    }

    _getPoints() {
        const seriesComparations = this._getSeriesComparations(
            this.visualization,
            this.$mdMedia("xs")
        );

        if (seriesComparations.length)
            return this._getComparationPoints(seriesComparations);
        else return [];
    }

    _limit(width, isXs, min = 1) {
        if (isXs) return 2;
        if (width == 100) return 5;
        return width <= 33 ? min : 3;
    }

    _getSerieId(visualization, isXs) {
        if (visualization.highlightsSeriesIds) {
            this._setHighlight(true);
            return visualization.highlightsSeriesIds;
        }

        // if (visualization.mainSerieId) {
        //     return visualization.mainSerieId;
        // }

        if (visualization.series.length > 1) {
            if (visualization.series.length <= 3) {
                return _.slice(visualization.series, 0, this._limit(100, isXs));
            } else if (visualization.series.length <= 5) {
                return _.slice(
                    visualization.series,
                    0,
                    this._limit(this.columnWidth, isXs, 0)
                );
            }

            return [];
        }

        return visualization.series;
    }

    _setHighlight(isHighlight) {
        this.isHighlight = isHighlight;
    }

    /**
     * @private
     * Set the series to show in comparation
     * @param {Object} customSeries
     * @param {String} highlightsSeriesIds
     * @param {Array} allSeries
     * @param {String} mainSerieId
     * @returns {Array}
     */
    _getSeriesComparations(visualization, isXs) {
        let seriesToComapartion = [].concat(
            this._getSerieId(visualization, isXs)
        );

        return this.SerieDomain.findCollection(
            { _id: { $in: seriesToComapartion } },
            {},
            "array"
        );
    }

    /**
     * @private
     * Set the series to show in comparation
     * @param {Object} customSeries
     * @param {String} highlightsSeriesIds
     * @param {Array} allSeries
     * @param {String} mainSerieId
     * @returns {Array}
     */
    async _getSeriesComparationsFromCloud(visualization, isXs) {
        let seriesToComapartion = [].concat(
            this._getSerieId(visualization, isXs)
        );

        return await this.SerieDomain.getByIds({
            seriesIds: seriesToComapartion,
            companyIds: [this.companyId]
        });
    }
}

export { IndicatorValuesComponent };

IndicatorValuesComponent.$inject = ["$mdMedia", "$stateParams", "$filter"];

const name = "indicatorValuesComponent";
const def = {
    bindings: {
        visualization: "<",
        currentPeriod: "<",
        currentFilterGroup: "<",
        currentComparation: "<",
        source: "<",
        fakeData: "<",
        seriesData: "<",
        isAccumulated: "<",
        companyData: "<",
        yearlyPoints: "<",
        budget: "<",
        columnWidth: "<"
    },
    controller: IndicatorValuesComponent,
    template
};
angular.module("nucont.shared").component(name, def);
export default { name, ...def };
